import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar, Plus, Clock, MapPin, Loader2, CalendarDays, List, Video, Building, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { MeetingCalendar } from '../components/MeetingCalendar';
import { DateProposals } from '../components/DateProposals';

const TIMEZONES = [
  'Africa/Johannesburg',
  'America/New_York',
  'America/Los_Angeles',
  'America/Chicago',
  'America/Mexico_City',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Asia/Dubai',
  'Australia/Sydney',
];

export default function MeetingsPage() {
  const { api, isChair } = useAuth();
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list, calendar, proposals
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newMeeting, setNewMeeting] = useState({
    title: '',
    date: '',
    time: '',
    description: '',
    reference_timezone: 'Africa/Johannesburg',
    status: 'draft',
    location_type: 'online',
    location_details: '',
    meeting_link: ''
  });

  const fetchMeetings = useCallback(async () => {
    try {
      const response = await api().get('/meetings');
      setMeetings(response.data);
    } catch (error) {
      console.error('Failed to fetch meetings:', error);
      toast.error('Failed to load meetings');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  const handleCreateMeeting = async () => {
    if (!newMeeting.title || !newMeeting.date) {
      toast.error('Please fill in required fields');
      return;
    }

    setCreating(true);
    try {
      const response = await api().post('/meetings', newMeeting);
      setMeetings([response.data, ...meetings]);
      setCreateDialogOpen(false);
      setNewMeeting({
        title: '',
        date: '',
        time: '',
        description: '',
        reference_timezone: 'Africa/Johannesburg',
        status: 'draft',
        location_type: 'online',
        location_details: '',
        meeting_link: ''
      });
      toast.success('Meeting created successfully');
      navigate(`/meetings/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create meeting');
    } finally {
      setCreating(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500/10 text-green-600 border-green-500/30';
      case 'completed': return 'bg-gray-500/10 text-gray-600 border-gray-500/30';
      default: return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30';
    }
  };

  const getLocationIcon = (meeting) => {
    if (meeting.location_type === 'online') return <Video className="h-4 w-4" />;
    if (meeting.location_type === 'physical') return <Building className="h-4 w-4" />;
    return <Globe className="h-4 w-4" />;
  };

  const getLocationText = (meeting) => {
    if (meeting.location_type === 'online') {
      return meeting.meeting_link ? 'Online Meeting' : 'Online (link TBD)';
    }
    if (meeting.location_type === 'physical') {
      return meeting.location_details || 'Physical (address TBD)';
    }
    return meeting.location_details || 'Hybrid';
  };

  const handleDateClick = (date, dayMeetings) => {
    if (dayMeetings.length === 1) {
      navigate(`/meetings/${dayMeetings[0].id}`);
    } else if (dayMeetings.length > 1) {
      // Could show a popover, for now just navigate to first
      navigate(`/meetings/${dayMeetings[0].id}`);
    }
  };

  const handleMeetingClick = (meeting) => {
    navigate(`/meetings/${meeting.id}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="meetings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Meetings</h1>
          <p className="text-muted-foreground mt-1">Manage your forum meetings</p>
        </div>
        
        {isChair && (
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="create-meeting-btn">
                <Plus className="h-4 w-4" />
                New Meeting
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Meeting</DialogTitle>
                <DialogDescription>
                  Set up a new forum meeting with date, time, and location.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Meeting Title *</Label>
                  <Input
                    id="title"
                    placeholder="e.g., February Forum Meeting"
                    value={newMeeting.title}
                    onChange={(e) => setNewMeeting({ ...newMeeting, title: e.target.value })}
                    data-testid="meeting-title-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date">Date *</Label>
                    <Input
                      id="date"
                      type="date"
                      value={newMeeting.date}
                      onChange={(e) => setNewMeeting({ ...newMeeting, date: e.target.value })}
                      data-testid="meeting-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="time">Time</Label>
                    <Input
                      id="time"
                      type="time"
                      value={newMeeting.time}
                      onChange={(e) => setNewMeeting({ ...newMeeting, time: e.target.value })}
                      data-testid="meeting-time-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="timezone">Reference Timezone</Label>
                  <Select
                    value={newMeeting.reference_timezone}
                    onValueChange={(value) => setNewMeeting({ ...newMeeting, reference_timezone: value })}
                  >
                    <SelectTrigger data-testid="meeting-timezone-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map((tz) => (
                        <SelectItem key={tz} value={tz}>
                          {tz.replace(/_/g, ' ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Location Section */}
                <div className="space-y-3 p-4 rounded-lg bg-muted/50">
                  <Label>Meeting Location</Label>
                  <Select
                    value={newMeeting.location_type}
                    onValueChange={(value) => setNewMeeting({ ...newMeeting, location_type: value })}
                  >
                    <SelectTrigger data-testid="meeting-location-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="online">
                        <div className="flex items-center gap-2">
                          <Video className="h-4 w-4" />
                          Online Meeting
                        </div>
                      </SelectItem>
                      <SelectItem value="physical">
                        <div className="flex items-center gap-2">
                          <Building className="h-4 w-4" />
                          Physical Location
                        </div>
                      </SelectItem>
                      <SelectItem value="hybrid">
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4" />
                          Hybrid (Both)
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {(newMeeting.location_type === 'online' || newMeeting.location_type === 'hybrid') && (
                    <div className="space-y-2">
                      <Label htmlFor="meeting_link">Meeting Link</Label>
                      <Input
                        id="meeting_link"
                        placeholder="https://zoom.us/j/... or Teams link"
                        value={newMeeting.meeting_link}
                        onChange={(e) => setNewMeeting({ ...newMeeting, meeting_link: e.target.value })}
                        data-testid="meeting-link-input"
                      />
                    </div>
                  )}
                  
                  {(newMeeting.location_type === 'physical' || newMeeting.location_type === 'hybrid') && (
                    <div className="space-y-2">
                      <Label htmlFor="location_details">Physical Address</Label>
                      <Textarea
                        id="location_details"
                        placeholder="123 Main St, City, Country"
                        value={newMeeting.location_details}
                        onChange={(e) => setNewMeeting({ ...newMeeting, location_details: e.target.value })}
                        data-testid="meeting-location-input"
                      />
                    </div>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Meeting description..."
                    value={newMeeting.description}
                    onChange={(e) => setNewMeeting({ ...newMeeting, description: e.target.value })}
                    data-testid="meeting-description-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={newMeeting.status}
                    onValueChange={(value) => setNewMeeting({ ...newMeeting, status: value })}
                  >
                    <SelectTrigger data-testid="meeting-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateMeeting} disabled={creating} data-testid="confirm-create-meeting-btn">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Create Meeting
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* View Toggle */}
      <Tabs value={view} onValueChange={setView} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="list" className="gap-2" data-testid="list-view-tab">
            <List className="h-4 w-4" />
            List
          </TabsTrigger>
          <TabsTrigger value="calendar" className="gap-2" data-testid="calendar-view-tab">
            <Calendar className="h-4 w-4" />
            Calendar
          </TabsTrigger>
          <TabsTrigger value="proposals" className="gap-2" data-testid="proposals-view-tab">
            <CalendarDays className="h-4 w-4" />
            Propose Dates
          </TabsTrigger>
        </TabsList>

        {/* List View */}
        <TabsContent value="list" className="mt-6">
          {meetings.length === 0 ? (
            <Card className="p-12">
              <div className="empty-state">
                <CalendarDays className="h-12 w-12 mb-4 text-muted-foreground/50" />
                <h3 className="text-lg font-semibold mb-2">No meetings yet</h3>
                <p className="text-muted-foreground mb-4">
                  {isChair ? 'Create your first forum meeting to get started.' : 'No meetings have been scheduled yet.'}
                </p>
                {isChair && (
                  <Button onClick={() => setCreateDialogOpen(true)} className="gap-2">
                    <Plus className="h-4 w-4" />
                    Create Meeting
                  </Button>
                )}
              </div>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {meetings.map((meeting, index) => (
                <Link 
                  key={meeting.id} 
                  to={`/meetings/${meeting.id}`}
                  className="block"
                  data-testid={`meeting-card-${index}`}
                >
                  <Card className="h-full hover:shadow-lg hover:border-primary/20 transition-all duration-200 cursor-pointer group">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-2">
                        <CardTitle className="text-lg line-clamp-2 group-hover:text-primary transition-colors">
                          {meeting.title}
                        </CardTitle>
                        <Badge variant="outline" className={getStatusColor(meeting.status)}>
                          {meeting.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span>{format(parseISO(meeting.date), 'EEEE, MMMM d, yyyy')}</span>
                      </div>
                      {meeting.time && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Clock className="h-4 w-4" />
                          <span>{meeting.time}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        {getLocationIcon(meeting)}
                        <span className="truncate">{getLocationText(meeting)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MapPin className="h-4 w-4" />
                        <span>{meeting.reference_timezone?.replace(/_/g, ' ')}</span>
                      </div>
                      {meeting.description && (
                        <CardDescription className="line-clamp-2 pt-1">
                          {meeting.description}
                        </CardDescription>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Calendar View */}
        <TabsContent value="calendar" className="mt-6">
          <MeetingCalendar 
            meetings={meetings} 
            onDateClick={handleDateClick}
            onMeetingClick={handleMeetingClick}
          />
        </TabsContent>

        {/* Date Proposals View */}
        <TabsContent value="proposals" className="mt-6">
          <DateProposals onDateSelected={fetchMeetings} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
